import { AuthError, authError } from './errors';
import { session } from './session';
import { authRequest } from './transport';
import {
  Authenticator,
  AuthSession,
  ChallengeResponse,
  Enrollment,
  LoginResponse,
  MessageResponse,
  SignInResponse
} from './types';

export async function checkEmail(email: string) {
  return session.exclusive(async () => {
    const browser = session.browserContext();
    const id = session.beginChallenge();
    const normalized = email.trim().toLowerCase();
    const data = await authRequest<ChallengeResponse>('/auth/check_email', {
      email: normalized,
      browser
    });
    if (!data.csrf_token)
      throw new AuthError('Invalid authentication response. Please start again.', 503);
    session.saveChallenge({
      id,
      email: normalized,
      csrf: data.csrf_token,
      stage: 'password',
      expiresAt: Date.now() + 300000,
      resendAt: 0,
      attempts: 0
    });
    return session.challenge;
  });
}

async function challengeRequest<T>(
  stage: 'password' | 'mfa',
  request: (csrf: string) => Promise<T>
): Promise<T> {
  const { challenge } = session;
  if (!challenge || challenge.stage !== stage)
    throw new AuthError('This sign-in challenge has expired. Please start again.', 401);
  try {
    return await request(challenge.csrf);
  } catch (error) {
    const failure = authError(error);
    if (!failure.status || failure.status >= 500) session.abandonChallenge();
    if (failure.status === 401 || failure.status === 403) {
      session.saveChallenge({ ...challenge, attempts: challenge.attempts + 1 });
    }
    throw failure;
  }
}

export async function signIn(password: string) {
  return session.exclusive(async () => {
    const { challenge } = session;
    const data = await challengeRequest('password', (csrf) =>
      authRequest<SignInResponse>(
        '/auth/sign_in',
        {
          email: challenge.email,
          password,
          browser: session.browserContext()
        },
        { csrf }
      )
    );
    if (!data.csrf_token || !['email', 'totp'].includes(data.mfa_method)) {
      session.abandonChallenge();
      throw new AuthError('Invalid authentication response. Please start again.', 503);
    }
    session.saveChallenge({
      ...challenge,
      csrf: data.csrf_token,
      stage: 'mfa',
      method: data.mfa_method,
      expiresAt: Date.now() + 300000,
      resendAt: Date.now() + 60000,
      attempts: 0
    });
    return session.challenge;
  });
}

export async function verifyOtp(code: string) {
  if (!/^\d{6}$/.test(code)) throw new AuthError('Enter a six-digit code.', 400);
  return session.exclusive(async () => {
    const data = await challengeRequest('mfa', (csrf) =>
      authRequest<LoginResponse>(
        '/auth/verify_otp',
        {
          code,
          browser: session.browserContext()
        },
        { csrf }
      )
    );
    try {
      session.acceptLogin(data);
    } catch (error) {
      session.clear();
      throw error;
    }
  });
}

export async function resendOtp() {
  return session.exclusive(async () => {
    const { challenge } = session;
    if (!challenge || challenge.stage !== 'mfa' || challenge.method !== 'email')
      throw new AuthError('Start a new email sign-in challenge.', 400);
    if (Date.now() < challenge.resendAt)
      throw new AuthError('Wait 60 seconds between code requests.', 429);
    const result = await authRequest<MessageResponse>('/auth/resend_otp', undefined, {
      csrf: challenge.csrf
    });
    session.saveChallenge({ ...challenge, resendAt: Date.now() + 60000 });
    return result;
  });
}

export const requestResetPassword = (email: string) =>
  authRequest<MessageResponse>('/auth/request_reset_password', {
    email: email.trim().toLowerCase()
  });
export const completeRecovery = (token: string, password: string) =>
  session.exclusive(async () => {
    const result = await authRequest<MessageResponse>('/auth/reset_password', { token, password });
    session.clear();
    return result;
  });
export const changePassword = (current_password: string, password: string) =>
  session.authenticated<MessageResponse>(
    '/auth/change_my_password',
    { current_password, password },
    'POST',
    true
  );
export const signOut = (all = false) =>
  session.authenticated<MessageResponse>(
    all ? '/auth/sign_out_all' : '/auth/sign_out',
    undefined,
    'POST',
    true
  );
export const getAuthenticators = () =>
  session.authenticated<{ data: Authenticator[] }>('/auth/mfa/authenticators', undefined, 'GET');
export const getSessions = () =>
  session.authenticated<{ data: AuthSession[] }>('/auth/sessions', undefined, 'GET');
export const enroll = (password: string) =>
  session.authenticated<Enrollment>('/auth/mfa/enroll', { password });
export const confirmEnrollment = (enrollment_token: string, code: string) =>
  session.authenticated<MessageResponse>(
    '/auth/mfa/confirm',
    { enrollment_token, code },
    'POST',
    true
  );
export const removeAuthenticator = (password: string, code: string) =>
  session.authenticated<MessageResponse>('/auth/mfa/remove', { password, code }, 'POST', true);
